import React, { useState, useEffect, useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import { Phone, Euro, Target, Calendar, AlertCircle, CheckCircle, Lock, MapPin, TrendingUp, Users, Menu, X } from 'lucide-react';
import '../styles/design-system.css';

// Shared utilities
import {
  formatCurrency,
  formatNumber,
  extractLocation,
  extractCustomerCode
} from '../utils/dashboardUtils';
import { 
  getDateRange, 
  getPreviousPeriodRange, 
  getPeriodLabel,
  convertAmsterdamToUTC
} from '../utils/dateUtils';
import { LEAD_CLASSIFICATIONS, CLASSIFICATION_EMOJIS, isLeadClassificationForAdmin } from '../constants/businessLogic';
import { dashboardCache } from '../utils/performanceCache';

// Data helpers (backend-API)
import { apiPost, setToken, hasValidToken } from '../utils/apiClient';
import { loadCustomerPrices, getPriceForDate, loadCustomerTargets, loadCallsData } from '../utils/supabaseHelpers';
import LeafletCallTrackingMap from './LeafletCallTrackingMap';
import AuthScreen from './shared/AuthScreen';

const ClientDashboard = () => {
  // Mobile menu toggle
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // CLIENT INFO DETECTION - Updated to work with /:clientCode
  const getClientInfoFromUrl = () => {
    const path = window.location.pathname;
    // Match both /M023 and /client-v2/M023 patterns
    const match = path.match(/\/(?:client-v2\/)?([A-Z]\d{3,4}[A-Za-z]?)/);
    
    if (!match) return null;
    
    const primaryCode = match[1];
    
    // Special case for M023 includes M023D  
    if (primaryCode === 'M023') {
      return { 
        primaryCode: 'M023', 
        allCodes: ['M023D', 'M023'],
        clientName: 'MMA'
      };
    }
    
    return { 
      primaryCode: primaryCode, 
      allCodes: [primaryCode],
      clientName: primaryCode 
    };
  };

  const [clientInfo, setClientInfo] = useState(getClientInfoFromUrl());
  
  // AUTH STATE - INLINE
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // DATA STATE
  const [data, setData] = useState({
    calls: [],
    previousCalls: [],
    clientData: [],
    priceMap: null,
    loading: true,
    error: null,
    _performance: null
  });
  
  // FILTERS - Initialize with URL parameters if present
  const getInitialFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDate = urlParams.get('startDate');
    const endDate = urlParams.get('endDate');
    
    if (startDate && endDate) {
      return {
        period: 'custom',
        startDate: startDate,
        endDate: endDate
      };
    }
    
    return {
      period: 'lastWeek'
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);

  // Read URL parameters for custom date range (also handle changes)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startDate = urlParams.get('startDate');
    const endDate = urlParams.get('endDate');
    
    if (startDate && endDate) {
      setFilters(prev => {
        // Only update if values actually changed
        if (prev.startDate !== startDate || prev.endDate !== endDate) {
          return {
            ...prev,
            period: 'custom',
            startDate: startDate,
            endDate: endDate
          };
        }
        return prev;
      });
    }
  }, []);

  // ANALYTICS - default values for when data isn't ready
  const defaultAnalytics = {
    totalInvestment: 0,
    totalCalls: 0,
    totalUnique: 0,
    totalLeads: 0,
    nonLeadCalls: 0,
    avgPerCall: 0,
    trends: [],
    classifications: [],
    locations: [],
    cumulativeComparison: [],
    previousPeriod: {
      totalInvestment: 0,
      totalCalls: 0,
      totalUnique: 0,
      totalLeads: 0
    }
  };

  // UTILITY FUNCTIONS
  const extractCustomerCodeFromCampaign = (campaignName) => {
    if (!campaignName || !clientInfo?.allCodes) return null;

    // First check if campaign contains any of the client's known codes
    const sortedCodes = clientInfo.allCodes.sort((a, b) => b.length - a.length);
    for (const code of sortedCodes) {
      if (campaignName.includes(code)) {
        return code;
      }
    }

    // Fallback to generic extraction
    return extractCustomerCode(campaignName);
  };

  const getPriceForCode = (code, callDate = null) => {
    // Try date-based price first
    if (data.priceMap && code && callDate) {
      const dateBasedPrice = getPriceForDate(data.priceMap, code, callDate);
      if (dateBasedPrice !== null) return dateBasedPrice;
    }
    // Fallback to customer_targets.price
    const clientData = data.clientData.find(client => client.customer_code === code);
    return clientData ? clientData.price : 0;
  };

  // AUTHENTICATION - INLINE
  const checkAuthentication = async () => {
    if (!clientInfo?.primaryCode) {
      setData(prev => ({ ...prev, error: 'Invalid client URL.' }));
      return;
    }

    // Bestaande sessie: geldige (niet-verlopen) JWT in localStorage.
    if (hasValidToken()) {
      setAuthenticated(true);
      return;
    }

    setAuthenticated(false);
  };

  const handlePasswordSubmit = async () => {
    try {
      const res = await apiPost('/login', {
        type: 'client',
        clientCode: clientInfo.primaryCode,
        password
      });

      // Bewaar de JWT; volgende data-reads gebruiken deze als Bearer-token.
      setToken(res.token);

      const relatedCodes = res.codes && res.codes.length ? res.codes : clientInfo.allCodes;
      setClientInfo(prev => ({
        ...prev,
        allCodes: relatedCodes,
        clientName: res.name || prev.clientName
      }));

      setAuthenticated(true);
      setAuthError('');

    } catch (error) {
      if (error.status === 401) {
        setAuthError('Invalid password');
      } else {
        setAuthError('Authentication failed');
      }
    }
  };

  // DATA LOADING WITH PAGINATION - Fixed to handle unlimited records
  const loadClientData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      if (!clientInfo?.primaryCode) {
        throw new Error('No valid client code found');
      }

      const clientCacheKey = `client_${clientInfo.primaryCode}_${JSON.stringify(filters)}`;
      const cached = dashboardCache.get(clientCacheKey);
      if (cached) {
        setData({ ...cached, _performance: { ...cached._performance, cacheUsed: true } });
        return;
      }

      const performanceStart = Date.now();

      const [allClientData, priceMapData] = await Promise.all([
        loadCustomerTargets(clientInfo.allCodes),
        loadCustomerPrices()
      ]);

      const currentRange = getDateRange(filters.period, filters.startDate, filters.endDate);
      const previousRange = getPreviousPeriodRange(filters.period, filters.startDate, filters.endDate);

      // Volledige rijen ophalen via backend (full=1), gescopet op client-codes.
      // getDateRange() retourneert al UTC Date-objecten.
      const getAllClientData = (startDate, endDate) =>
        loadCallsData(startDate, endDate, {}, {
          clientCodes: clientInfo.allCodes,
          full: 1,
        });

      const [currentCalls, previousCalls] = await Promise.all([
        getAllClientData(currentRange.start, currentRange.end),
        getAllClientData(previousRange.start, previousRange.end)
      ]);

      const totalTime = Date.now() - performanceStart;

      const resultData = {
        calls: currentCalls || [],
        previousCalls: previousCalls || [],
        clientData: allClientData || [],
        priceMap: priceMapData || null,
        loading: false,
        error: null,
        _performance: {
          totalTime,
          recordCount: (currentCalls?.length || 0) + (previousCalls?.length || 0),
          currentRecords: currentCalls?.length || 0,
          previousRecords: previousCalls?.length || 0,
          cacheUsed: false
        }
      };

      dashboardCache.set(clientCacheKey, resultData);
      setData(resultData);

    } catch (error) {
      console.error('Client data loading error:', error);
      setData(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  // ANALYTICS PROCESSING - Memoized for performance
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analytics = useMemo(() => {
    // Return defaults if data isn't ready
    if (!data.clientData.length) return defaultAnalytics;

    const processCallsData = (calls) => {
      if (!calls.length) return {
        totalInvestment: 0,
        totalCalls: 0,
        totalUnique: 0,
        totalLeads: 0,
        nonLeadCalls: 0,
        avgPerCall: 0,
        trends: [],
        classifications: [],
        locations: []
      };

      let totalInvestment = 0;
      let totalCalls = calls.length;
      let totalLeads = 0;

      // Eigen unique-telling per (campaign, from-nummer) tuple binnen filter-periode.
      // Sinds 24 mei 2026: vervangt Qooqie's lifetime is_recurring vlag.
      const totalUniqueSet = new Set();

      const dailyStats = {};
      const classificationStats = {};
      const locationStats = {};

      calls.forEach(call => {
        const classification = call.classification || '';
        const campaignName = call.campaign || '';
        const location = extractLocation(campaignName);
        const callDate = new Date(call.start).toISOString().split('T')[0];
        const fromKey = call.from && call.from !== 'anonymous'
          ? call.from
          : (call.from === 'anonymous' ? `anonymous:${call.start}` : null);
        const uniqKey = fromKey && campaignName ? `${campaignName}|${fromKey}` : null;

        if (!dailyStats[callDate]) {
          dailyStats[callDate] = {
            date: callDate,
            totalCalls: 0,
            unique: 0,
            uniqueSet: new Set(),
            leads: 0
          };
        }

        if (!classificationStats[classification]) {
          classificationStats[classification] = {
            type: classification,
            count: 0,
            emoji: CLASSIFICATION_EMOJIS[classification] || '📞'
          };
        }

        if (!locationStats[location]) {
          locationStats[location] = {
            location: location,
            calls: 0,
            unique: 0,
            uniqueSet: new Set(),
            leads: 0,
            investment: 0
          };
        }

        dailyStats[callDate].totalCalls++;
        classificationStats[classification].count++;
        locationStats[location].calls++;

        if (uniqKey) {
          totalUniqueSet.add(uniqKey);
          dailyStats[callDate].uniqueSet.add(uniqKey);
          locationStats[location].uniqueSet.add(uniqKey);
        }

        const specificCode = extractCustomerCodeFromCampaign(campaignName);
        const price = specificCode ? getPriceForCode(specificCode, call.start) : 0;

        if (price > 0 && isLeadClassificationForAdmin(classification, call.is_recurring)) {
          totalInvestment += price;
          locationStats[location].investment += price;
        }

        if (isLeadClassificationForAdmin(classification, call.is_recurring)) {
          totalLeads++;
          dailyStats[callDate].leads++;
          locationStats[location].leads++;
        }
      });

      const avgPerCall = totalCalls > 0 ? totalInvestment / totalCalls : 0;

      // Convert Sets naar counts in output
      const trends = Object.values(dailyStats)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map(d => ({
          date: d.date,
          totalCalls: d.totalCalls,
          unique: d.uniqueSet ? d.uniqueSet.size : 0,
          leads: d.leads,
        }));
      const locations = Object.values(locationStats)
        .map(l => ({
          ...l,
          unique: l.uniqueSet ? l.uniqueSet.size : 0,
          uniqueSet: undefined,
        }))
        .sort((a, b) => b.investment - a.investment);

      return {
        totalInvestment,
        totalCalls,
        totalUnique: totalUniqueSet.size,
        totalLeads,
        nonLeadCalls: totalCalls - totalLeads,
        avgPerCall,
        trends,
        classifications: Object.values(classificationStats).filter(c => c.count > 0),
        locations,
      };
    };

    const currentAnalytics = processCallsData(data.calls || []);
    const previousAnalytics = processCallsData(data.previousCalls || []);

    // Create cumulative data
    const previousByDate = {};
    previousAnalytics.trends.forEach(trend => {
      previousByDate[trend.date] = trend;
    });

    const combinedTrends = [];

    currentAnalytics.trends.forEach(currentTrend => {
      const previousTrend = previousByDate[currentTrend.date] || {
        leads: 0,
        totalCalls: 0,
        unique: 0
      };

      combinedTrends.push({
        date: currentTrend.date,
        leads: currentTrend.leads || 0,
        totalCalls: currentTrend.totalCalls || 0,
        unique: currentTrend.unique || 0,
        previousLeads: previousTrend.leads || 0,
        previousTotalCalls: previousTrend.totalCalls || 0,
        previousUnique: previousTrend.unique || 0
      });
    });

    // For single-day periods, add previous period data as a separate point
    if (currentAnalytics.trends.length === 1 && previousAnalytics.trends.length === 1) {
      const previousDate = previousAnalytics.trends[0].date;
      combinedTrends.unshift({
        date: previousDate,
        leads: previousAnalytics.trends[0].leads || 0,
        totalCalls: previousAnalytics.trends[0].totalCalls || 0,
        unique: previousAnalytics.trends[0].unique || 0,
        previousLeads: 0,
        previousTotalCalls: 0,
        previousUnique: 0
      });
    }

    let cumulativeCurrentLeads = 0;
    let cumulativeCurrentTotalCalls = 0;
    let cumulativeCurrentUnique = 0;
    let cumulativePreviousLeads = 0;
    let cumulativePreviousTotalCalls = 0;
    let cumulativePreviousUnique = 0;

    const cumulativeComparison = combinedTrends.map(trend => {
      cumulativeCurrentLeads += trend.leads;
      cumulativeCurrentTotalCalls += trend.totalCalls;
      cumulativeCurrentUnique += trend.unique;
      cumulativePreviousLeads += trend.previousLeads;
      cumulativePreviousTotalCalls += trend.previousTotalCalls;
      cumulativePreviousUnique += trend.previousUnique;

      return {
        ...trend,
        cumulativeCurrentLeads,
        cumulativeCurrentTotalCalls,
        cumulativeCurrentUnique,
        cumulativePreviousLeads,
        cumulativePreviousTotalCalls,
        cumulativePreviousUnique
      };
    });

    return {
      ...currentAnalytics,
      cumulativeComparison,
      previousPeriod: {
        totalInvestment: previousAnalytics.totalInvestment,
        totalCalls: previousAnalytics.totalCalls,
        totalUnique: previousAnalytics.totalUnique,
        totalLeads: previousAnalytics.totalLeads
      }
    };
  }, [data.calls, data.previousCalls, data.clientData, data.priceMap, clientInfo?.allCodes]);

  // EFFECTS
  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo?.primaryCode]);

  useEffect(() => {
    if (authenticated && clientInfo?.primaryCode) {
      if (filters.period) {
        dashboardCache.clear();
      }
      loadClientData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, filters.period, filters.startDate, filters.endDate, clientInfo?.primaryCode]);

  // URL VALIDATION
  if (!clientInfo?.primaryCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Invalid URL - Client code not found</p>
          <p className="text-gray-500 text-sm">Expected format: /M023</p>
        </div>
      </div>
    );
  }

  // AUTH SCREEN - Using shared component with red theme
  if (!authenticated) {
    return (
      <AuthScreen
        auth={{
          password,
          setPassword,
          handlePasswordSubmit,
          error: authError
        }}
        title={data.clientData[0]?.name || clientInfo?.clientName}
        subtitle="LEAD MAGNATE DASHBOARD"
        error={data.error}
        theme="red"
      />
    );
  }

  // LOADING STATE - Design system
  if (data.loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">LOADING DASHBOARD...</p>
          <p className="loading-subtext">{data.clientData[0]?.name || clientInfo?.clientName}</p>
        </div>
      </div>
    );
  }

  // ERROR STATE - Design system
  if (data.error) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <AlertCircle className="error-icon" />
          <p className="error-message">ERROR: {data.error}</p>
          <button
            onClick={() => {
              dashboardCache.clear();
              loadClientData();
            }}
            className="btn-primary"
          >
            RETRY
          </button>
        </div>
      </div>
    );
  }

  // CALCULATIONS
  const investmentComparison = analytics.previousPeriod.totalInvestment > 0 ? 
    (((analytics.totalInvestment - analytics.previousPeriod.totalInvestment) / analytics.previousPeriod.totalInvestment) * 100).toFixed(1) : null;
  
  const callsComparison = analytics.previousPeriod.totalCalls > 0 ? 
    (((analytics.totalCalls - analytics.previousPeriod.totalCalls) / analytics.previousPeriod.totalCalls) * 100).toFixed(1) : null;

  const uniqueComparison = analytics.previousPeriod.totalUnique > 0 ? 
    (((analytics.totalUnique - analytics.previousPeriod.totalUnique) / analytics.previousPeriod.totalUnique) * 100).toFixed(1) : null;

  const leadsComparison = analytics.previousPeriod.totalLeads > 0 ? 
    (((analytics.totalLeads - analytics.previousPeriod.totalLeads) / analytics.previousPeriod.totalLeads) * 100).toFixed(1) : null;

  // KPI CARD COMPONENT - Design system
  const KPICard = ({ title, value, subtitle, icon: Icon, comparison = null }) => (
    <div className="kpi-card">
      <div className="kpi-card-content">
        <div className="kpi-icon-wrapper">
          <Icon className="kpi-icon" />
        </div>
        <div className="kpi-data">
          <p className="kpi-title">{title}</p>
          <p className="kpi-value">{value}</p>
          {subtitle && <p className="kpi-subtitle">{subtitle}</p>}
          {comparison && (
            <div className={`kpi-comparison ${
              comparison > 0 ? 'positive' : comparison < 0 ? 'negative' : ''
            }`}>
              <TrendingUp className={`kpi-trend-icon ${comparison < 0 ? 'rotate-180' : ''}`} />
              {comparison > 0 ? '+' : ''}{comparison}% vorige periode
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // MAIN DASHBOARD
  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          
          /* Print CSS Shadow Suppression - Fix voor Mac Preview/iPhone Safari */
          *, *::before, *::after {
            box-shadow: none !important;
            text-shadow: none !important;
            filter: none !important;
          }
          
          .period-selector { display: none !important; }
          .dashboard-page-1 { page-break-after: always; }
          .map-section { 
            page-break-before: always; 
            page-break-after: always;
            height: 100vh !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .map-section .max-w-7xl {
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            height: 100vh !important;
          }
          .map-section .bg-white {
            height: 100vh !important;
            margin: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Specifieke fixes voor Leaflet map componenten */
          .leaflet-container,
          .leaflet-control-container,
          .leaflet-marker-icon,
          .leaflet-popup,
          .leaflet-tooltip {
            box-shadow: none !important;
            filter: none !important;
          }
          
          .locations-section { page-break-before: always; }
          .calls-section { page-break-before: always; }
        }
      `}</style>

      <div className="theme-container">
        <div className="dashboard-page-1">
          <div className="dashboard-header">
            <div className="dashboard-header-content">
              <div className="header-left">
                {/* Mobile menu toggle */}
                <button
                  className="mobile-menu-toggle"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>

                {data.clientData[0]?.logo_url && (
                  <div className="client-logo-wrapper">
                    <img
                      src={data.clientData[0].logo_url}
                      alt={data.clientData[0].name}
                      className="client-logo"
                    />
                  </div>
                )}
                <div className="header-titles">
                  <h1 className="dashboard-title">{data.clientData[0]?.name || clientInfo?.clientName}</h1>
                  <p className="dashboard-subtitle">LEAD MAGNATE dashboard</p>
                </div>
              </div>
              <div className="header-right">
                <a
                  href="https://cookies.nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-link"
                >
                  COOKIES BV
                </a>
              </div>
            </div>
          </div>

          <div className="dashboard-content">
            {/* Desktop Filter Bar */}
            <div className="filter-bar filter-bar-desktop period-selector">
              <div className="filter-group">
                <Calendar className="filter-icon" />
                <select
                  value={filters.period}
                  onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
                  className="filter-select"
                >
                  <option value="today">Vandaag</option>
                  <option value="yesterday">Gisteren</option>
                  <option value="thisWeek">Deze week</option>
                  <option value="lastWeek">Vorige week</option>
                  <option value="thisMonth">Deze maand</option>
                  <option value="lastMonth">Vorige maand</option>
                  <option value="last30days">Laatste 30 dagen</option>
                </select>
              </div>

              <div className="filter-status">
                <div className="status-row">
                  <CheckCircle className="status-icon" />
                  {getPeriodLabel(filters.period)} • {formatNumber(analytics.totalCalls)} CALLS
                </div>
                {data._performance && (
                  <div className="performance-info">
                    ⚡ {data._performance.cacheUsed ? 'Cached' : `${data._performance.totalTime}ms load`} • {data._performance.recordCount} records
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Filter Menu */}
            {mobileMenuOpen && (
              <div className="filter-bar filter-bar-mobile period-selector">
                <div className="filter-group">
                  <Calendar className="filter-icon" />
                  <select
                    value={filters.period}
                    onChange={(e) => {
                      setFilters(prev => ({ ...prev, period: e.target.value }));
                      setMobileMenuOpen(false);
                    }}
                    className="filter-select"
                  >
                    <option value="today">Vandaag</option>
                    <option value="yesterday">Gisteren</option>
                    <option value="thisWeek">Deze week</option>
                    <option value="lastWeek">Vorige week</option>
                    <option value="thisMonth">Deze maand</option>
                    <option value="lastMonth">Vorige maand</option>
                    <option value="last30days">Laatste 30 dagen</option>
                  </select>
                </div>
              </div>
            )}

            <div className="kpi-grid">
              <KPICard
                title="CALLS"
                value={formatNumber(analytics.totalCalls)}
                subtitle={`${analytics.totalCalls - analytics.totalLeads} non-leads`}
                icon={Phone}
                comparison={callsComparison}
              />
              <KPICard
                title="UNIEK"
                value={formatNumber(analytics.totalUnique)}
                subtitle="unieke oproepen"
                icon={Users}
                comparison={uniqueComparison}
              />
              <KPICard
                title="LEADS"
                value={formatNumber(analytics.totalLeads)}
                subtitle="totaal"
                icon={Target}
                comparison={leadsComparison}
              />
              <KPICard
                title="INVESTERING"
                value={formatCurrency(analytics.totalInvestment)}
                subtitle="totaal"
                icon={Euro}
                comparison={investmentComparison}
              />
            </div>

            <div className="charts-grid">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">CALLS DAGELIJKS - {getPeriodLabel(filters.period)}</h3>
                </div>
                <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(date) => new Date(date).toLocaleDateString('nl-NL', { month: 'numeric', day: 'numeric' })}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => {
                        const nameMapping = { 'leads': 'Leads', 'unique': 'Uniek', 'totalCalls': 'Calls' };
                        const displayName = nameMapping[name] || name;
                        return [formatNumber(value), displayName];
                      }}
                      itemSorter={(item) => {
                        if (!item) return 99;
                        const orderByKey = { totalCalls: 0, unique: 1, leads: 2 };
                        const key = (item.dataKey || item.name || '').toString();
                        return orderByKey[key] ?? 99;
                      }}
                      labelFormatter={(date) => new Date(date).toLocaleDateString('nl-NL')}
                    />
                    <Legend payload={[
                      { value: 'Calls', type: 'line', color: '#5F5E5E' },
                      { value: 'Uniek', type: 'line', color: '#31B9CE' },
                      { value: 'Leads', type: 'line', color: '#D02E17' }
                    ]} />
                    <Line type="monotone" dataKey="totalCalls" stroke="#5F5E5E" strokeWidth={2} name="Calls" />
                    <Line type="monotone" dataKey="unique" stroke="#31B9CE" strokeWidth={2} name="Uniek" />
                    <Line type="monotone" dataKey="leads" stroke="#D02E17" strokeWidth={3} name="Leads" />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">CALLS CUMULATIEF - {getPeriodLabel(filters.period)}</h3>
                </div>
                <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.cumulativeComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(date) => new Date(date).toLocaleDateString('nl-NL', { month: 'numeric', day: 'numeric' })}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => {
                        const nameMapping = {
                          'cumulativeCurrentLeads': 'Leads',
                          'cumulativeCurrentUnique': 'Unique',
                          'cumulativeCurrentTotalCalls': 'Calls'
                        };
                        return [formatNumber(value), nameMapping[name] || name];
                      }}
                      itemSorter={(item) => {
                        if (!item) return 99;
                        const orderByKey = {
                          cumulativeCurrentTotalCalls: 0,
                          cumulativeCurrentUnique: 1,
                          cumulativeCurrentLeads: 2
                        };
                        const key = (item.dataKey || item.name || '').toString();
                        return orderByKey[key] ?? 99;
                      }}
                      labelFormatter={(date) => new Date(date).toLocaleDateString('nl-NL')}
                    />
                    <Legend payload={[
                      { value: 'Calls', type: 'square', color: '#5F5E5E' },
                      { value: 'Unique', type: 'square', color: '#31B9CE' },
                      { value: 'Leads', type: 'square', color: '#D02E17' }
                    ]} />
                    <Area type="monotone" dataKey="cumulativeCurrentTotalCalls" stackId="3" stroke="#5F5E5E" fill="#5F5E5E" fillOpacity={0.5} name="Calls" />
                    <Area type="monotone" dataKey="cumulativeCurrentUnique" stackId="2" stroke="#31B9CE" fill="#31B9CE" fillOpacity={0.6} name="Unique" />
                    <Area type="monotone" dataKey="cumulativeCurrentLeads" stackId="1" stroke="#D02E17" fill="#D02E17" fillOpacity={0.7} name="Leads" />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Map sectie */}
        <div className="map-section">
          <div className="dashboard-content">
            <div className="card map-card">
              <LeafletCallTrackingMap
                callData={data.calls}
                height="640px"
                clientInfo={clientInfo}
                customerTargets={data.clientData || []}
                priceMap={data.priceMap}
                onLocationClick={(location) => {
                }}
              />
            </div>
          </div>
        </div>

        {/* Locaties sectie */}
        <div className="locations-section">
          <div className="dashboard-content">
            {analytics.locations && analytics.locations.length > 0 && (
              <div className="card table-card">
                <div className="card-header">
                  <h3 className="card-title">
                    <MapPin className="card-title-icon" />
                    PER LOCATIE
                  </h3>
                </div>
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>PLAATS</th>
                        <th>CALLS</th>
                        <th>UNIEK</th>
                        <th>LEADS</th>
                        <th>INVESTERING</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.locations.map((location, index) => (
                        <tr key={index}>
                          <td className="location-cell">
                            📍 {location.location.toUpperCase()}
                          </td>
                          <td className="number-cell">
                            {formatNumber(location.calls)}
                          </td>
                          <td className="number-cell">
                            {formatNumber(location.unique)}
                          </td>
                          <td className="number-cell">
                            {formatNumber(location.leads)}
                          </td>
                          <td className="currency-cell">
                            {formatCurrency(location.investment)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Calls Table */}
            <div className="card table-card calls-section">
              <div className="card-header">
                <h3 className="card-title">
                  <Phone className="card-title-icon" />
                  OPROEPEN
                </h3>
              </div>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>DATUM</th>
                      <th>TIJD</th>
                      <th>PLAATS</th>
                      <th>AFZENDER</th>
                      <th>DUUR</th>
                      <th>STATUS</th>
                      <th>OPNAME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.calls
                      .sort((a, b) => new Date(b.start) - new Date(a.start))
                      .map((call, index) => {
                        const callDate = new Date(call.start.replace(' ', 'T') + 'Z');
                        const dateStr = callDate.toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' });
                        const timeStr = callDate.toLocaleTimeString('nl-NL', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                          timeZone: 'Europe/Amsterdam'
                        });
                        const location = extractLocation(call.campaign || '').toUpperCase();
                        const duration = call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '-';
                        const isRecurring = call.is_recurring ? '🔄' : '🆕';
                        const recordingUrl = call.recording_download_url || call.recording_url || call.recording || '';

                        return (
                          <tr key={index}>
                            <td className="date-cell">{dateStr}</td>
                            <td className="time-cell">{timeStr}</td>
                            <td className="location-cell">{location}</td>
                            <td className="caller-cell">{call.caller_id || call.from || '-'}</td>
                            <td className="duration-cell">{duration}</td>
                            <td className="status-cell">{isRecurring}</td>
                            <td className="recording-cell">
                              {recordingUrl ? (
                                <a href={recordingUrl} target="_blank" rel="noopener noreferrer" className="recording-link">
                                  ▶️
                                </a>
                              ) : (
                                <span className="no-recording">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dashboard-footer">
              <div className="footer-content">
                <p className="footer-title">
                  <strong>{data.clientData[0]?.name || clientInfo?.clientName}</strong> DASHBOARD POWERED BY <strong>LEAD MAGNATE</strong>
                </p>
                <p className="footer-link">
                  <a href="https://cookies.nl" target="_blank" rel="noopener noreferrer">COOKIES BV</a>
                </p>
                <div className="footer-stats">
                  <span>📍 {analytics.locations.length} LOCATIES</span>
                  <span className="footer-divider">•</span>
                  <span>📊 {formatNumber(analytics.totalCalls)} CALLS</span>
                  <span className="footer-divider">•</span>
                  <span>🎯 {formatNumber(analytics.totalLeads)} LEADS</span>
                  <span className="footer-divider">•</span>
                  <span>💰 {formatCurrency(analytics.totalInvestment)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientDashboard;
